import React from "react";
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  StatusBar,
} from "react-native";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { router, Stack } from "expo-router";
import { Ionicons } from "@expo/vector-icons";

interface ProgressItem {
  id: string;
  label: string;
  badge: string;
  isCompleted: boolean;
  isNext: boolean;
}

const PROGRESS_ITEMS: ProgressItem[] = [
  {
    id: "class-actions",
    label: "Class action claims",
    badge: "Up to $1087",
    isCompleted: true,
    isNext: false,
  },
  {
    id: "unclaimed-money",
    label: "Unclaimed money search",
    badge: "Average $32 one-time",
    isCompleted: true,
    isNext: false,
  },
  {
    id: "ai-refund",
    label: "AI refund assistant",
    badge: "Up to $31/mo",
    isCompleted: true,
    isNext: false,
  },
  {
    id: "money-tracking",
    label: "Money tracking",
    badge: "Up to $53/year",
    isCompleted: false,
    isNext: true,
  },
];

const EnableMoneyTrackingScreen = () => {
  const handleContinue = () => {
    router.push("/signup/money-tracking-intro");
  };

  const renderCompletedItem = (item: ProgressItem) => (
    <View key={item.id} style={styles.itemRow}>
      <View style={styles.checkIconContainer}>
        <Ionicons name="checkmark" size={18} color="#0F9129" />
      </View>
      <Text style={styles.itemLabel}>{item.label}</Text>
      <View style={styles.badge}>
        <Text style={styles.badgeText}>{item.badge}</Text>
      </View>
    </View>
  );

  const renderNextItem = () => {
    const nextItem = PROGRESS_ITEMS.find((item) => item.isNext);

    if (!nextItem) return null;

    return (
      <View style={styles.timelineWrapper}>
        {/* Vertical line */}
        <View style={styles.verticalLineContainer}>
          <View style={styles.verticalLine} />
          <View style={styles.greenDot} />
        </View>

        {/* Content */}
        <View style={styles.timelineContent}>
          {/* Next item card */}
          <View style={styles.nextItemCard}>
            <Text style={styles.nextLabel}>Next</Text>
            <View style={styles.nextItemContent}>
              <Text style={styles.itemLabel}>{nextItem.label}</Text>
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{nextItem.badge}</Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false, gestureEnabled: false }} />
      <StatusBar barStyle="dark-content" />
      <SafeAreaView style={styles.safeArea}>
        {/* Content */}
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.headerSection}>
            <Text style={styles.title}>
              Next, let's help manage{"\n"}your spending
            </Text>
            <Text style={styles.subtitle}>
              In just a few steps, you'll unlock all your benefits and start
              saving money.
            </Text>
          </View>

          {/* Progress Items */}
          <View style={styles.itemsList}>
            {PROGRESS_ITEMS.filter((item) => item.isCompleted).map(renderCompletedItem)}
            {renderNextItem()}
          </View>
        </View>

        {/* Spacer */}
        <View style={styles.spacer} />

        {/* Bottom Section */}
        <View style={styles.bottomSection}>
          <View style={styles.buttonContainer}>
            <ActionButton title="Continue" onPress={handleContinue} />
          </View>
          <Text style={styles.disclaimer}>*All numbers are estimated</Text>
        </View>
      </SafeAreaView>
    </View>
  );
};

export default EnableMoneyTrackingScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  safeArea: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  headerSection: {
    gap: 24,
    marginBottom: 24,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 32,
    lineHeight: 38,
    color: "#161D14",
    letterSpacing: -0.64,
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 16,
    lineHeight: 24,
    color: "rgba(22, 29, 20, 0.7)",
    letterSpacing: -0.32,
  },
  itemsList: {
    gap: 20,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  checkIconContainer: {
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  itemLabel: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 15,
    lineHeight: 23,
    color: "#161D14",
  },
  badge: {
    backgroundColor: "#E1EFE4",
    paddingHorizontal: 8,
    borderRadius: 100,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
  },
  badgeText: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 12,
    lineHeight: 25,
    color: "#161D14",
    letterSpacing: -0.24,
    textAlign: "center",
  },
  timelineWrapper: {
    flexDirection: "row",
    gap: 8,
  },
  verticalLineContainer: {
    width: 24,
    alignItems: "center",
  },
  verticalLine: {
    width: 2,
    height: 14,
    backgroundColor: "#0F9129",
  },
  greenDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#0F9129",
  },
  timelineContent: {
    flex: 1,
  },
  nextItemCard: {
    backgroundColor: "#F3F7F3",
    borderRadius: 8,
    padding: 16,
    gap: 2,
  },
  nextLabel: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 13,
    lineHeight: 15,
    color: "#0F9129",
    letterSpacing: -0.26,
  },
  nextItemContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  spacer: {
    flex: 1,
  },
  bottomSection: {
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: "#F3F3F3",
    paddingTop: 12,
  },
  buttonContainer: {
    marginBottom: 12,
  },
  disclaimer: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    lineHeight: 19,
    color: "rgba(22, 29, 20, 0.7)",
    textAlign: "center",
  },
});
